import React, { DragEvent, FC, Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { Location } from "history";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { TFunction, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, RouteComponentProps, useHistory, useLocation } from "react-router-dom";
import { apiOrganization } from "src/services/Api/Organization/apiOrganization";
import { convertListStatusToEntityStatus } from "src/utils/entityStatus/entityStatus";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import Tag from "go-app/components/Tags/Tag";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import RenderLimitedText from "go-core/components/RenderLimitedText";
import { StickyColumnWithEntityStatus } from "go-core/components/StickyColumnWithEntityStatus";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import { ListFilterSource } from "go-list/core/components/Filter/services/types";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig, ListParamsType, MultipleActionsParams } from "go-list/list/services/types";
import { hasPermission, selectOrganization } from "go-security/services/organizations/selectors";
import { OrganizationState } from "go-security/services/organizations/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext, SegmentListService } from "go-segment/context";
import AppFormatResourceStatusLabel from "../../../../../../../../../../components/FormatStatus";
import { ReactComponent as PadlockClosedSVG } from "../../../../../../../../../../images/svg/documents/padlock-closed.svg";
import { ReactComponent as PadlockOpenedSVG } from "../../../../../../../../../../images/svg/documents/padlock-opened.svg";
import { PurchaseApi } from "../../../../../../../../../../services/Api/Organization/types";
import ImportDocumentModal from "../../../../components/ImportDocumentModal";
import { PurchaseSummaryApi } from "../../../../services/types";
import { purchaseListName, purchaseResourceType } from "../../../../utils";
import { getConfigFieldsWithSummaryData } from "../../../../utils/summaryDataUtils";
import QuantityCircle from "../../components/QuantityCircle";

const TranslationsInitialized = () => {
	const { t } = useTranslation();
	t("enums.common.status.UNLINKED");
	t("enums.common.status.DRAFT");
	t("enums.common.status.PARTIALLY_RECEIVED");
	t("enums.common.status.RECEIVED");
	t("enums.common.status.VOID");
	t("enums.common.status.REVOKED");
};

interface ListState {
	resource?: Record<string, any>;
	mobileActions: MobileActionProps[];
}

const buildConfig = (
	t: TFunction,
	organization: OrganizationState,
	data: any,
	onRemove: (
		items: PurchaseApi[],
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => Promise<void>,
	onRestore: (
		items: PurchaseApi[],
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => Promise<void>,
	onLock: (
		items: PurchaseApi[],
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => Promise<void>,
	onUnlock: (
		items: PurchaseApi[],
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => Promise<void>,
	onConfirm: (
		items: PurchaseApi[],
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => Promise<void>,
	onCancel: (
		items: PurchaseApi[],
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => Promise<void>,
	onReceive: (
		items: PurchaseApi[],
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => Promise<void>,
	onDeleteReceives: (
		items: PurchaseApi[],
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => void,
	location: Location,
	segmentListService: SegmentListService,
	summaryData: PurchaseSummaryApi | undefined,
	setSummaryData: (summaryData: PurchaseSummaryApi) => void,
	hasLockPermission: boolean,
	hasRemovePermission: boolean
) => {
	let config = {
		fields: [
			{
				id: "purchased_at",
				name: t("common.word.date", { ns: "lib" }),
				type: "date",
				render: (item: PurchaseApi) => (
					<StickyColumnWithEntityStatus
						status={convertListStatusToEntityStatus(item.status)}
						to={`${location.pathname}/${item.id}`}
					>
						{FormatDate(item?.purchased_at)}
					</StickyColumnWithEntityStatus>
				),
				renderExport: (item: PurchaseApi) => FormatDate(item?.purchased_at),
			},
			{
				id: "created_at",
				name: t("common.word.created_at", { ns: "lib" }),
				type: "date",
				render: (item: PurchaseApi) => FormatDate(item?.created_at),
			},
			{
				id: "document_number",
				name: t("common.word.number", { ns: "lib" }),
				type: "text",
				render: (item: PurchaseApi) => (
					<div className="d-flex align-items-center">
						<span className="me-2">
							<Link to={`${location.pathname}/${item.id}`}>{item?.document_number}</Link>
						</span>
						<OverlayTrigger
							placement="top"
							overlay={
								<Tooltip id={`tooltip-${item?.document_number}`}>
									{t(`enums.common.status.${item?.lock_status}`)}
								</Tooltip>
							}
						>
							{item.lock_status === "LOCKED" ? <PadlockClosedSVG /> : <PadlockOpenedSVG />}
						</OverlayTrigger>
					</div>
				),
				renderExport: (item: PurchaseApi) => item.document_number,
			},
			{
				id: "channels",
				name: t("common.word.channel"),
				render: (item: PurchaseApi) => item?.channel?.name,
			},
			{
				id: "contact",
				name: t("common.word.contact"),
				render: (item: PurchaseApi) => {
					const contact = item?.contact ? item.contact.name : item.external_contact_name;
					return <RenderLimitedText minWidth={100}>{contact}</RenderLimitedText>;
				},
				renderExport: (item: PurchaseApi) => (item?.contact ? item.contact.name : item.external_contact_name),
				styleOverride: {
					width: "20%",
				},
			},
			{
				id: "stock",
				name: t("common.word.stock"),
				render: (item: PurchaseApi) => item?.stock?.name,
			},
			{
				id: "status",
				name: t("common.word.status", { ns: "lib" }),
				render: (item: PurchaseApi) => <AppFormatResourceStatusLabel status={item?.status} />,
				renderExport: (item: PurchaseApi) => t(`enums.common.status.${item?.status}`),
			},
			{
				id: "comment",
				name: t("common.word.comment.title"),
				type: "text",
				render: (item: PurchaseApi) => {
					return <RenderLimitedText minWidth={200}>{item.comment}</RenderLimitedText>;
				},
				renderExport: (item: PurchaseApi) => item.comment,
			},
			{
				id: "quantity",
				name: t("modules.purchase.field.purchased_received.title"),
				render: (item: PurchaseApi) => {
					const { quantity, quantity_received } = item;
					const text = `${quantity}/${quantity_received}`;
					if (quantity_received === 0) return <QuantityCircle text={text} type="dark" />;
					else if (quantity_received > 0 && quantity_received === quantity)
						return <QuantityCircle text={text} />;
					return <QuantityCircle text={text} type="semi" />;
				},
				renderExport: (item: PurchaseApi) => `${item?.quantity}/${item?.quantity_received}`,
			},
			{
				id: "tagQuery",
				name: t("common.word.tags"),
				render: (item: PurchaseApi) => (
					<div className="d-flex flex-wrap">
						{item?.tags?.map((tag, i) => (
							<Tag className="mb-1" key={`tag-${i}`} title={tag.name} />
						))}
					</div>
				),
				renderExport: (item: PurchaseApi) => item?.tags?.map((tag) => tag.name).join(", "),
			},
			{
				id: "total_price_net_money",
				name: t("modules.purchase.field.total_price_net_money.title"),
				type: "number",
				render: (item: PurchaseApi) => FormatMoney(item?.total_price_net_money),
			},
			{
				id: "reference_number",
				name: t("common.word.reference_number"),
				type: "text",
			},
			{
				id: "created_by",
				name: t("common.word.created_by"),
				render: (item: PurchaseApi) => item.created_by || t("common.word.system"),
			},
		],
		actions: [
			{
				name: t("lib:common.action.preview"),
				link: (item: PurchaseApi) => `${location.pathname}/${item.id}`,
			},
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: (item: PurchaseApi) => onRemove([item]),
				visible: (item: PurchaseApi) => hasRemovePermission,
				refreshListAfter: true,
			},
			{
				name: t("common.action.restore", { ns: "lib" }),
				click: (item: PurchaseApi) => onRestore([item]),
				visible: (item: PurchaseApi) => item.status === "VOID" && hasRemovePermission,
				refreshListAfter: true,
			},
			{
				name: t("modules.purchase.actions.lock.title"),
				click: (item: PurchaseApi) => onLock([item]),
				visible: (item: PurchaseApi) => item.lock_status !== "LOCKED" && hasLockPermission,
				refreshListAfter: true,
			},
			{
				name: t("modules.purchase.actions.unlock.title"),
				click: (item: PurchaseApi) => onUnlock([item]),
				visible: (item: PurchaseApi) => item.lock_status !== "UNLOCKED" && hasLockPermission,
				refreshListAfter: true,
			},
			{
				name: t("common.action.confirm", { ns: "lib" }),
				click: (item: PurchaseApi) => onConfirm([item]),
				visible: (item: PurchaseApi) => item.status === "DRAFT",
				refreshListAfter: true,
			},
			{
				name: t("common.action.cancel", { ns: "lib" }),
				click: (item: PurchaseApi) => onCancel([item]),
				visible: (item: PurchaseApi) => item.status !== "DRAFT",
				refreshListAfter: true,
			},
			{
				name: t("modules.purchase.action.collect.title"),
				click: (item: PurchaseApi) => onReceive([item]),
				visible: (item: PurchaseApi) => !item.shippable,
				refreshListAfter: true,
			},
			{
				name: t("modules.purchase.actions.delete_receives.title"),
				click: (item: PurchaseApi) => onDeleteReceives([item]),
				visible: (item: PurchaseApi) => item.status === "RECEIVED",
				refreshListAfter: true,
			},
			{
				name: t("common.action.void"),
				click: (item: PurchaseApi) => onRemove([item]),
				visible: (item: PurchaseApi) =>
					item.status !== "DRAFT" && item.status !== "VOID" && item.lock_status === "UNLOCKED",
				refreshListAfter: true,
			},
		],
		multipleActions: [
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: (
					multipleActionsItems: PurchaseApi[],
					_: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => onRemove(multipleActionsItems, areAllItemsSelected, multipleActionParams),
				refreshListAfter: true,
				visible: () => hasRemovePermission,
			},
			{
				name: t("common.action.restore", { ns: "lib" }),
				click: (
					multipleActionsItems: PurchaseApi[],
					_: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => onRestore(multipleActionsItems, areAllItemsSelected, multipleActionParams),
				visible: (multipleActionsItems: PurchaseApi[]) =>
					multipleActionsItems.filter((item) => item.status === "VOID").length > 0 && hasRemovePermission,
				refreshListAfter: true,
			},
			{
				name: t("modules.purchase.actions.lock.title"),
				click: (
					multipleActionsItems: PurchaseApi[],
					_: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => onLock(multipleActionsItems, areAllItemsSelected, multipleActionParams),
				visible: (multipleActionsItems: PurchaseApi[]) =>
					multipleActionsItems.filter((item) => item.lock_status !== "LOCKED").length > 0 &&
					hasLockPermission,
				refreshListAfter: true,
			},
			{
				name: t("modules.purchase.actions.unlock.title"),
				click: (
					multipleActionsItems: PurchaseApi[],
					_: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => onUnlock(multipleActionsItems, areAllItemsSelected, multipleActionParams),
				visible: (multipleActionsItems: PurchaseApi[]) =>
					multipleActionsItems.filter((item) => item.lock_status !== "UNLOCKED").length > 0 &&
					hasLockPermission,
				refreshListAfter: true,
			},
			{
				name: t("common.action.confirm", { ns: "lib" }),
				click: (
					multipleActionsItems: PurchaseApi[],
					_: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => onConfirm(multipleActionsItems, areAllItemsSelected, multipleActionParams),
				visible: (multipleActionsItems: PurchaseApi[]) =>
					multipleActionsItems.filter((item) => item.status === "DRAFT").length > 0,
				refreshListAfter: true,
			},
			{
				name: t("common.action.cancel", { ns: "lib" }),
				click: (
					multipleActionsItems: PurchaseApi[],
					_: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => onCancel(multipleActionsItems, areAllItemsSelected, multipleActionParams),
				visible: (multipleActionsItems: PurchaseApi[]) =>
					multipleActionsItems.filter((item) => item.status !== "DRAFT").length > 0,
				refreshListAfter: true,
			},
			{
				name: t("modules.purchase.action.collect.title"),
				click: (
					multipleActionsItems: PurchaseApi[],
					_: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => onReceive(multipleActionsItems, areAllItemsSelected, multipleActionParams),
				visible: (multipleActionsItems: PurchaseApi[]) =>
					multipleActionsItems.filter((item) => item.status === "OPEN" && !item.shippable).length > 0,
				refreshListAfter: true,
			},
			{
				name: t("modules.purchase.actions.delete_receives.title"),
				click: (
					multipleActionsItems: PurchaseApi[],
					_: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => onDeleteReceives(multipleActionsItems, areAllItemsSelected, multipleActionParams),
				visible: (multipleActionsItems: PurchaseApi[]) =>
					multipleActionsItems.filter((item) => item.status === "RECEIVED").length > 0,
				refreshListAfter: true,
			},
			{
				name: t("common.action.void"),
				click: (
					multipleActionsItems: PurchaseApi[],
					_: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionsParams?: MultipleActionsParams
				) => onRemove(multipleActionsItems, areAllItemsSelected, multipleActionsParams),
				visible: (items: PurchaseApi[]) =>
					items.filter(
						(item) => item.status !== "DRAFT" && item.status !== "VOID" && item.lock_status === "UNLOCKED"
					).length > 0,
				refreshListAfter: true,
			},
		],
		selectedColumns: [
			"purchased_at",
			"document_number",
			"reference_number",
			"channels",
			"contact",
			"stock",
			"status",
			"quantity",
			"tagQuery",
			"total_price_net_money",
		],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
			{
				id: "unclosed",
				name: t("modules.purchase.field.unclosed.title"),
				slug: "unclosed",
				filters: [
					{
						filterId: "statuses",
						value: `PARTIALLY_RECEIVED${FILTER_VALUE_SEPARATOR}OPEN${FILTER_VALUE_SEPARATOR}DRAFT${FILTER_VALUE_SEPARATOR}UNLINKED`,
						condition: "e",
					},
				],
			},
		],
		filters: [
			{
				id: "channels",
				name: t("common.word.channel"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getChannelSearchSelect(search, params, { cancelToken: options?.token }),
				} as ListFilterSource,
			},
			{
				id: "contactIds",
				name: t("common.word.contact"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getContactSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				} as ListFilterSource,
			},
			{
				id: "tagQuery",
				name: t("common.word.tags"),
				type: "search_select",
				source: {
					request: async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
						const res = await apiOrganization.getTagSearchSelect(search, params, {
							cancelToken: options?.token,
						});
						return res.map((r) => {
							return { ...r, id: r.label };
						});
					},
				} as ListFilterSource,
			},
			{
				id: "stockIds",
				name: t("common.word.stock"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getStockSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "lock_status",
				name: t("modules.purchase.field.blockade.title"),
				type: "list",
				listOptions: {
					LOCKED: t("enums.common.status.LOCKED"),
					UNLOCKED: t("enums.common.status.UNLOCKED"),
				},
				hasDefaultOptions: false,
			},
			{
				id: "statuses",
				name: t("common.word.status", { ns: "lib" }),
				type: "list",
				listOptions: {
					UNLINKED: t("enums.common.status.UNLINKED"),
					DRAFT: t("enums.common.status.DRAFT"),
					OPEN: t("enums.common.status.OPEN"),
					PARTIALLY_RECEIVED: t("enums.common.status.PARTIALLY_RECEIVED"),
					RECEIVED: t("enums.common.status.RECEIVED"),
					VOID: t("enums.common.status.VOID"),
					REVOKED: t("enums.common.status.REVOKED"),
				},
				hasDefaultOptions: false,
			},
		],
		exportConfig: {
			title: t("modules.purchase.field.export_config.title"),
			filename: t("modules.purchase.header.title"),
			organization: organization.name,
		},
		selectedSegment: getSelectedSegmentForListConfig(data.segments || [], "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "stock,contact,tags,channel,custom_fields";
			const response = await apiOrganization.getPurchases(params, { cancelToken: sourceToken?.token });
			setSummaryData(response.summary);
			return response;
		},
		fetchTotalItemsCountForSelectedFilters: async (
			params: Record<string, any> = {},
			sourceToken?: CancelTokenSource
		) => {
			params.include = "stock,contact,tags,channel,custom_fields";

			return apiOrganization.getPurchasesCount(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => {
			return segmentListService.save(purchaseListName, purchaseResourceType, segment);
		},
		numberOfStickyColumnsAtTheStart: 1,
		shouldDisableSortingOfStickyColumns: false,
		isNumberOfStickyColumnsDynamic: true,
		doesIdColumnRedirectToPreviewPage: true,
	} as ListConfig;

	const summaryDataConfig = {
		total_price_net_money: FormatMoney(summaryData?.total_price_net_money),
	};

	config.fields = getConfigFieldsWithSummaryData(t, summaryDataConfig, config.fields, summaryData);
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};

	return config;
};
const List: FC<ListState> = ({ resource, mobileActions }) => {
	const { t } = useTranslation();
	const history = useHistory();
	const location = useLocation();
	const organization = useSelector(selectOrganization);
	const [summaryData, setSummaryData] = useState<PurchaseSummaryApi | undefined>(undefined);
	const segmentContextValue = React.useContext(SegmentContext);
	const confirmation = useConfirmation();
	const { addSuccessFlash } = useFlash();
	const hasLockPermission = useSelector(hasPermission(["PURCHASE_ORDER_MANAGE", "PURCHASE_ORDER_LOCK"]));
	const hasRemovePermission = useSelector(hasPermission(["PURCHASE_ORDER_MANAGE", "PURCHASE_ORDER_DELETE"]));

	if (!resource) return null;

	const onRemove = async (
		multipleActionsItems: PurchaseApi[],
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const idsAsString = multipleActionsItems.map((item) => item.id.toString()).join(",");

		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.remove", { ns: "lib" }),
		});

		await apiOrganization.removePurchases(idsAsString, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t(`common.flash.removed`, { ns: "lib" }));
	};

	const onRestore = async (
		multipleActionsItems: PurchaseApi[],
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const idsAsString = multipleActionsItems.map((item) => item.id.toString()).join(",");

		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.activate", { ns: "lib" }),
		});

		await apiOrganization.restorePurchases(idsAsString, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t(`common.flash.restored`, { ns: "lib" }));
	};

	const onLock = async (
		multipleActionsItems: PurchaseApi[],
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const idsAsString = multipleActionsItems.map((item) => item.id.toString()).join(",");
		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});

		await apiOrganization.lockPurchases(idsAsString, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t(`common.flash.completed`, { ns: "lib" }));
	};

	const onUnlock = async (
		multipleActionsItems: PurchaseApi[],
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const idsAsString = multipleActionsItems.map((item) => item.id.toString()).join(",");

		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});

		await apiOrganization.unlockPurchases(idsAsString, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t(`common.flash.completed`, { ns: "lib" }));
	};

	const onConfirm = async (
		multipleActionsItems: PurchaseApi[],
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const idsAsString = multipleActionsItems.map((item) => item.id.toString()).join(",");

		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});

		await apiOrganization.confirmPurchases(idsAsString, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t(`common.flash.completed`, { ns: "lib" }));
	};

	const onCancel = async (
		multipleActionsItems: PurchaseApi[],
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const idsAsString = multipleActionsItems.map((item) => item.id.toString()).join(",");

		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.cancel", { ns: "lib" }),
		});

		await apiOrganization.cancelPurchases(idsAsString, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t(`common.flash.completed`, { ns: "lib" }));
	};

	const onReceive = async (
		multipleActionsItems: PurchaseApi[],
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const idsAsString = multipleActionsItems.map((item) => item.id.toString()).join(",");

		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});

		await apiOrganization.receivePurchases(idsAsString, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t(`common.flash.completed`, { ns: "lib" }));
	};

	const onDeleteReceives = async (
		items: PurchaseApi[],
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const idsAsString = items.map((item) => item.id.toString()).join(",");

		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});
		await apiOrganization.deletePurchasesReceive(idsAsString, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
	};

	const data = resource.read();
	const config = buildConfig(
		t,
		organization,
		data,
		onRemove,
		onRestore,
		onLock,
		onUnlock,
		onConfirm,
		onCancel,
		onReceive,
		onDeleteReceives,
		location,
		segmentContextValue,
		summaryData,
		setSummaryData,
		hasLockPermission,
		hasRemovePermission
	);

	return (
		<ListData
			config={config}
			emptyList={{ addAction: () => history.push(`${location.pathname}/new`) }}
			canManageMultiActionsForAllItems
			shouldFilterAvailableMultipleActions
			mobileActions={mobileActions}
			shouldChangeSegmentFromOutside
		/>
	);
};

const OrganizationOrderPurchaseIndexPage: FC<RouteComponentProps> = (props) => {
	const [resource, setResource] = useState<Record<string, any>>();
	const [resourceFromFile, setResourceFromFile] = useState<PurchaseApi | undefined>(undefined);
	const [modalActive, setModalActive] = useState<boolean>(false);
	const [uploadedFile, setuploadedFile] = useState<File | undefined>(undefined);
	const segmentContextValue = useContext(SegmentContext);
	const { t } = useTranslation();
	const history = useHistory();
	const organization = useSelector(selectOrganization);
	const customFieldResourceTypes = [
		{
			type: purchaseResourceType,
			name: t(`enums.custom_fields.resources.PURCHASE_ORDER`),
		},
	];
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(t("modules.purchase.header.title"));
		setResource(
			wrapPromise(segmentContextValue.get(purchaseListName, purchaseResourceType, customFieldResourceTypes))
		);
	}, []);

	useEffect(() => {
		if (resourceFromFile && uploadedFile)
			history.push({
				pathname: `${props.match.url}/preview-from-file`,
				state: { resourceFromFile, uploadedFile },
			});
	}, [resourceFromFile, uploadedFile]);

	const buttons: ButtonProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: `/${organization.id}/documents/purchases/new`,
			variant: "primary",
		},
		{
			title: t("modules.document.action.add_from_file.title"),
			action: () => setModalActive(true),
			dropdown: true,
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: `/${organization.id}/documents/purchases/new`,
		},
		{
			title: t("modules.document.action.add_from_file.title"),
			action: () => setModalActive(true),
		},
	];

	const fileActionHandler = (resource: PurchaseApi, uploadedFile: File) => {
		setResourceFromFile(resource);
		setuploadedFile(uploadedFile);
	};

	const fileDropHandler = (e: DragEvent<HTMLElement>) => {
		e.preventDefault();
		const files = e.dataTransfer?.files;
		if (files && files[0]) {
			const uploadedFile = files[0];
			setModalActive(true);
			setuploadedFile(uploadedFile);
		}
	};

	const dragOverHandler = (e: DragEvent<HTMLElement>) => e.preventDefault();

	const dragEnterHandler = (e: DragEvent<HTMLElement>) => e.preventDefault();

	const dragLeaveHandler = (e: DragEvent<HTMLElement>) => e.preventDefault();

	return (
		<>
			{modalActive && (
				<ImportDocumentModal
					onClose={() => setModalActive(false)}
					onFileActionPerformed={(resource, fileUploaded) =>
						fileActionHandler(resource as PurchaseApi, fileUploaded)
					}
					previewFromFileRequest={(data) => apiOrganization.previewPurchaseFromFile(data)}
					saveFromFileRequest={(data) => apiOrganization.savePurchaseFromFile(data)}
					file={uploadedFile}
					documentPathToRedirect="purchases"
				/>
			)}
			<div
				onDrop={fileDropHandler}
				onDragOver={dragOverHandler}
				onDragEnter={dragEnterHandler}
				onDragLeave={dragLeaveHandler}
			>
				{!isMobile && <Header title={t("modules.purchase.header.title")} buttons={buttons} />}
				<Suspense fallback={<LoadingContainer />}>
					<List resource={resource} mobileActions={mobileActions} />
				</Suspense>
			</div>
		</>
	);
};
export default OrganizationOrderPurchaseIndexPage;
